import { useCallback, useMemo, useRef, useState } from 'react'
import { getJobStatusActions, JOB_ACTION_INTENT } from '../utils/jobStatus'
import { isCircuitExternalJob } from '../../../common/reportOutcome'

// Historia 120 (Bloque 26, paquete P15, ENMIENDA 05/D-SP22/D-SP23) --
// `features/S223-seleccion-en-la-tabla-jupiter.feature`, `features/S106-selector-inicio-en-lote-jupiter.feature`
// (S106-S111/S113-S115 reescritos), `features/S211-preevaluacion-selector-jupiter.feature` (S211/S218/
// S219/S222 reescritos).
//
// ⛔ El selector modal (`BatchJobPickerDialog.jsx`) SE RETIRA ENTERO. La selección múltiple pasa a
// vivir EN LA PROPIA TABLA de la lista principal del widget: una casilla por fila y el botón
// "Iniciar en lote" en la cabecera (D-SP22). Este fichero es el único dueño del mecanismo.
//
// 🔴 D-SP23 -- LA FORMA DE LA FILA TAMBIÉN ES CONTRATO (§7.2 #18 ampliado/RN8.10): la fila de la cola
// (`contrato-sun.md` §3) NO tiene identificador de primer nivel -- trae `fabricationOrder:{id}`
// anidada y `jobReferenceId`. La selección múltiple de `generic/SmartMateriaUITable.jsx` identifica
// una fila por el VALOR de sus `primaryKeys` (por defecto `['id']`, reducido con
// `reduceRowPropertiesToDesired`, comparado por `JSON.stringify`) -- con el valor por defecto TODA
// fila se reduce a `{}`, así que dos clics distintos dejan CERO marcadas (el defecto real que el PO
// vio). Por eso este envoltorio de datos añade a cada fila una CLAVE SINTÉTICA de primer nivel,
// `rowKey = "<fabricationOrderId>:<jobReferenceId>"`, y la instancia de la tabla declara
// `primaryKeys: ['rowKey']` -- nunca el valor por defecto. `SmartMateriaUITable.jsx` NO se toca
// (S114): la clave la calcula el CLIENTE con dos campos que la fila YA publica (§8.4); no se le pide
// nada nuevo a Sun.

/** D-SP23 -- the field name this instance declares as its own `primaryKeys` (S114: only THIS
 * instance; the generic table keeps its `['id']` default everywhere else). */
export const BATCH_SELECTION_ROW_KEY_FIELD = 'rowKey'

/** RN8.1 -- the row-local flag `extraFilterParameter` (`generic/SmartMateriaUITable.jsx`) reads to
 * disable just one row's checkbox, without hiding or filtering the row out of the list (POL3: this
 * is UX prevention, never the guarantee -- that stays server-side, in the previsualización). */
export const BATCH_SELECTION_NOT_STARTABLE_FIELD = '__notStartableToday'

/**
 * D-SP23 -- the synthetic key, built from the two fields the row already publishes. Never the job
 * reference alone (R-15: the contract never promises it is unique ACROSS fabrication orders).
 *
 * @param {Object} row
 * @returns {string}
 */
export const buildBatchSelectionRowKey = (row = {}) => `${row?.fabricationOrder?.id}:${row?.jobReferenceId}`

/**
 * The inverse of `buildBatchSelectionRowKey`: parses a reduced `rowKey` value back into the pair N1
 * wants. `reduceRowPropertiesToDesired` (`generic/SmartMateriaUITable.jsx:767`) stringifies whatever
 * it reduces, so both halves travel back through `Number(...)` -- the ids are always numeric on the
 * wire (`contrato-sun.md` §3).
 *
 * @param {string} rowKey
 * @returns {{fabricationOrderId: number, jobReferenceId: number}}
 */
export const parseBatchSelectionRowKey = (rowKey = '') => {
  const [fabricationOrderId, jobReferenceId] = String(rowKey).split(':')
  return { fabricationOrderId: Number(fabricationOrderId), jobReferenceId: Number(jobReferenceId) }
}

/**
 * RN8.1 -- whether a row of the planned queue offers ITS OWN start action TODAY, read from the
 * PUBLISHED state alone (never `available`, never a count of pieces, never the `ALLOW_START_ANY_JOB`
 * setting -- Sun already resolves that setting INSIDE the published literal, POL2/RN16). This is capa
 * (a), kept WHOLE from the retired picker (D-SP22 point 2): governed-by-acceptance and
 * external-circuit jobs never offer a start action of their own either.
 *
 * 🔒 Formulación canónica (S111): la casilla está habilitada EXACTAMENTE cuando la fila ofrece hoy su
 * acción de arranque, ni más ni menos.
 *
 * @param {Object} row
 * @returns {boolean}
 */
export const isJobSelectableForBatchStart = (row = {}) => {
  if (row.governedByAcceptance === true) return false
  if (isCircuitExternalJob(row)) return false
  return getJobStatusActions(row.status).some((action) => action.intent === JOB_ACTION_INTENT.START)
}

/**
 * D-SP23/S222 -- the widget's OWN data wrapper around `usePlannedJobs().fetchPlannedJobs` (or any
 * sibling fetcher resolving the same `Page<CompassJobReferenceDto>` shape). Adds the synthetic
 * `rowKey` and the local-selectability flag to every row of `content`, and preserves every OTHER
 * field of the `Page` (`totalElements`, `totalPages`, `number`, `size`, `first`, `last`, `empty`,
 * `pageable`, `sort`) untouched, so pagination keeps working (S222/S223).
 *
 * 🔒 S222 -- a bare array is NEVER trusted as row content, not even a non-empty one: it is either
 * `usePlannedJobs`'s own error fallback (`[]`), or the shape of the ORIGINAL defect this story's
 * ancestor amendment fixed. Either way it resolves to ZERO rows here, never to the rows it carried.
 *
 * @param {Object|Array} response Whatever `fetchPage` resolved.
 * @returns {Object} A `Page`-shaped object with `content` replaced.
 */
export const wrapPlannedJobsPageForBatchSelection = (response) => {
  const isBareArray = Array.isArray(response)
  const content = isBareArray ? [] : (Array.isArray(response?.content) ? response.content : [])
  const wrappedContent = content.map((row) => ({
    ...row,
    [BATCH_SELECTION_ROW_KEY_FIELD]: buildBatchSelectionRowKey(row),
    [BATCH_SELECTION_NOT_STARTABLE_FIELD]: !isJobSelectableForBatchStart(row)
  }))
  const metadata = isBareArray ? {} : response
  return { ...metadata, content: wrappedContent }
}

/**
 * D-SP23/S231 -- the value `generic/SmartMateriaUITable.jsx:188` puts in `primaryKeys` when an
 * instance declares none, which is what the queue's request carried before this story. Duplicated
 * here as a literal on purpose: the generic table does not export it, and S114 keeps that file
 * untouched.
 */
export const CONTRACT_PAGINATION_PRIMARY_KEYS = ['id']

/**
 * S231/§8.4 ("cero contrato: la clave sintética NUNCA cruza la frontera") -- restores the request
 * body to the one this widget sent BEFORE the story (the golden), field by field, so the queue route
 * sees exactly the contract it has always seen (§8.2) whether the pagination handed here is the
 * table's own load/page-change/refresh one (`requestPagination`) or the EXTRA "how many are
 * selectable" one that `generic/SmartMateriaUITable.jsx:945` fires whenever there is a selection
 * (`requestPaginationMultiSelection`). Both converge on the same body: the extra call becomes a
 * harmless, redundant repeat of the queue's own request, never a broken filter.
 *
 * 🔴 RESTAURA, NO BORRA -- y la diferencia tumbó la lista entera. `rows` and `allSelected` are NOT
 * artifacts of this story: `generic/hooks/usePagination.jsx:52-54` has always put `rows: []`,
 * `allSelected: false` and `primaryKeys` on EVERY paginated body, and Sun reads `rows` without a null
 * check (`PaginationBuilder.getRowsSplit:123` → `data.size()`), so deleting them answers every
 * request with a `NullPointerException`. What this story adds is only their selection-borne VALUES
 * (`rows` filled with the synthetic key's values, `allSelected`, and the `extraFilterName`/
 * `extraFilterValue` pair that `usePaginationMultiSelection.jsx` derives from this instance's own
 * `extraFilterParameter`) -- plus `primaryKeys: ['rowKey']`, a CLIENT-only identity no column of
 * `PlannerDetailModificationEntity` answers to. So the three list-shaped fields are RESET to their
 * pre-story values (never removed, never left empty) and only the `extraFilter*` pair -- which the
 * pre-story body did not have at all -- is removed.
 *
 * @param {Object} pagination
 * @returns {Object}
 */
export const restoreContractPaginationBody = (pagination = {}) => {
  const restored = { ...pagination }
  restored.rows = []
  restored.allSelected = false
  restored.primaryKeys = [...CONTRACT_PAGINATION_PRIMARY_KEYS]
  delete restored.extraFilterName
  delete restored.extraFilterValue
  return restored
}

const EMPTY_SELECTION = new Set()

/**
 * Historia 120 (enmienda 05) -- the batch selection that lives ABOVE the table (D-SP22/D-SP23):
 * identified by the (fabricationOrderId, jobReferenceId) pair, materialised as the synthetic rowKey,
 * surviving pagination and the widget's own automatic refresh (RN8.5/S223) until the caller's
 * explicit `resetSelection()` (RN8.9/RN12/S226).
 *
 * @param {Function} fetchPage `usePlannedJobs().fetchPlannedJobs`-shaped fetcher.
 * @returns {Object}
 */
const useBatchJobSelection = (fetchPage) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState(EMPTY_SELECTION)
  const [resetTick, setResetTick] = useState(0)
  const knownRowsRef = useRef(new Map())

  // RN8.9/RN12/S226 -- the ONLY way this selection is cleared: a confirmed start with at least one
  // job actually started, a machine change or an operator change. Uses the table's OWN external
  // reset prop (`resetMultipleSelectionValue`, `generic/SmartMateriaUITable.jsx`) -- the file itself
  // is never touched (S114).
  const resetSelection = useCallback(() => {
    knownRowsRef.current = new Map()
    setSelectedRowKeys(EMPTY_SELECTION)
    setResetTick((tick) => tick + 1)
  }, [])

  // D-SP23/S222 -- wraps the raw page, remembers every row this opening has ever seen (so a
  // confirmed batch can be recased into FULL rows, never just the pair), and RN8.7/S224 -- a row
  // VISIBLE on the page just fetched that no longer offers its start action silently drops out of
  // the selection; a marked pair whose row is NOT on this page is never touched here (the client
  // cannot tell "another page" from "no longer in the queue" without paging through everything --
  // that is the previsualización's job, RN18/POL4).
  const dataFetch = useCallback(async (pagination) => {
    // S231 -- restored to the pre-story body BEFORE it ever reaches `fetchPage` (`usePlannedJobs`'s
    // `apiPost`, `contrato-sun.md` §8.2/§8.4): the wire never sees the selection's values, and
    // `rows`/`primaryKeys` are never null nor absent.
    const response = await fetchPage(restoreContractPaginationBody(pagination))
    const wrapped = wrapPlannedJobsPageForBatchSelection(response)
    wrapped.content.forEach((row) => {
      knownRowsRef.current.set(row[BATCH_SELECTION_ROW_KEY_FIELD], row)
    })
    setSelectedRowKeys((previous) => {
      if (previous.size === 0) return previous
      let changed = false
      const next = new Set(previous)
      wrapped.content.forEach((row) => {
        const key = row[BATCH_SELECTION_ROW_KEY_FIELD]
        if (row[BATCH_SELECTION_NOT_STARTABLE_FIELD] && next.has(key)) {
          next.delete(key)
          changed = true
        }
      })
      return changed ? next : previous
    })
    return wrapped
  }, [fetchPage])

  // RN8.6/S218 -- the header's inverted-selection gesture (`{rows: [], allSelected: true}`, "the
  // whole dataset", unfiltered, no materialised rows) is ignored WHOLE: it never marks, never
  // counts, never confirms and it never erases what was already marked (D-SP22 point 6). The single-
  // click "select this page" gesture is NOT affected (it never sets `allSelected`).
  // `SmartMateriaUITable.jsx` is not touched: the gesture is neutralised here, in the widget's own
  // callback.
  const onMultipleSelectionChange = useCallback((value) => {
    if (value?.allSelected === true) return
    setSelectedRowKeys(new Set(Array.isArray(value?.rows) ? value.rows.map((row) => row[BATCH_SELECTION_ROW_KEY_FIELD]) : []))
  }, [])

  // D-SP23 -- the pairs N1 wants, parsed straight from the synthetic key (RN8.5's materialisation).
  const selectedPairs = useMemo(() => Array.from(selectedRowKeys).map(parseBatchSelectionRowKey), [selectedRowKeys])

  // D-SP23 -- recasing the reduced rowKeys against the rows this opening has actually fetched, so the
  // caller gets FULL rows (needed to resolve the first started job back to a `node`, S126) without a
  // second lookup call. A getter, deliberately NOT a memo: `knownRowsRef` is mutated in place by every
  // `dataFetch` resolution (including the widget's own periodic refresh), so this must read it at the
  // exact instant the caller asks, never a snapshot from whenever `selectedRowKeys` last changed.
  const getSelectedRows = useCallback(
    () => Array.from(selectedRowKeys).map((key) => knownRowsRef.current.get(key)).filter(Boolean),
    [selectedRowKeys]
  )

  return {
    dataFetch,
    onMultipleSelectionChange,
    resetSelection,
    resetTick,
    selectedCount: selectedRowKeys.size,
    selectedPairs,
    getSelectedRows
  }
}

export default useBatchJobSelection
